import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./AdsCabinet.scss";
import DynamicHeader from "../../Header/DynamicHeader";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

async function fetchJson(url, options) {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...(options || {}),
  });
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const msg = (data && data.error) || (data && data.message) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function localInputToIso(v) {
  if (!v) return "";
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return "";
  return new Date(t).toISOString();
}

// report date helpers (YYYY-MM-DD)
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toDayKeyLocal(d) {
  // local date -> YYYY-MM-DD
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function todayKeyLocal() {
  return toDayKeyLocal(new Date());
}
function daysAgoKeyLocal(n) {
  const d = new Date();
  d.setDate(d.getDate() - Number(n || 0));
  return toDayKeyLocal(d);
}

// форматування
function formatNumber(n) {
  const x = Number(n || 0);
  try {
    return x.toLocaleString("uk-UA");
  } catch {
    return String(x);
  }
}

function formatDayUk(dayKey) {
  if (!dayKey || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return dayKey || "";
  const d = new Date(`${dayKey}T00:00:00`);
  try {
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
  }
}

function formatDateTimeUk(iso) {
  if (!iso) return "-";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "-";
  const d = new Date(t);
  try {
    return d.toLocaleString("uk-UA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(
      d.getMinutes()
    )}`;
  }
}

// copy helper (clipboard)
async function copyToClipboard(text) {
  const s = String(text || "");
  if (!s) throw new Error("Немає що копіювати");
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch {}
  // fallback
  try {
    const ta = document.createElement("textarea");
    ta.value = s;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, s.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (!ok) throw new Error("execCommand copy failed");
    return true;
  } catch (e) {
    throw new Error("Не вдалося скопіювати");
  }
}

export default function AdsCabinet() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  // upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);

  const [form, setForm] = useState({
    title: "",
    targetUrl: "",
    placement: "feed_inline_1",
    startAt: "",
    endAt: "",
    status: "paused",
    imageUrl: "",
  });

  // report state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCampaignId, setReportCampaignId] = useState("");
  const [reportFrom, setReportFrom] = useState(daysAgoKeyLocal(6)); // останні 7 днів включно
  const [reportTo, setReportTo] = useState(todayKeyLocal());
  const [reportLoading, setReportLoading] = useState(false);
  const [reportErr, setReportErr] = useState("");
  const [reportData, setReportData] = useState(null);

  // share state
  const [shareLoading, setShareLoading] = useState(false);
  const [shareErr, setShareErr] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareOk, setShareOk] = useState("");

  const placements = useMemo(
    () => [
      { key: "feed_inline_1", label: "Стрічка: банер #1 (вбудований)" },
      { key: "feed_inline_2", label: "Стрічка: банер #2 (вбудований)" },
      { key: "profile_top", label: "Профіль: зверху" },
      { key: "listing_details_bottom", label: "Оголошення: знизу" },
    ],
    []
  );

  const placementLabelByKey = useMemo(() => {
    const m = {};
    placements.forEach((p) => (m[p.key] = p.label));
    return m;
  }, [placements]);

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const data = await fetchJson(`${API_BASE}/api/ads/campaigns?status=all`);
      setItems((data && data.items) || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
  };

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFile(f);
    setUploadInfo(null);
    setForm((p) => ({ ...p, imageUrl: "" }));
  };

  // upload to S3 через бекенд
  const uploadBannerViaBackend = async () => {
    if (!file) {
      setErr("Оберіть файл зображення");
      return;
    }

    setErr("");
    setUploading(true);
    setUploadInfo(null);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${API_BASE}/api/ads/upload`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });

      const ct = res.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

      if (!res.ok) {
        const msg =
          (data && data.error) ||
          (data && data.message) ||
          (typeof data === "string" ? data : null) ||
          `Upload failed (${res.status})`;
        throw new Error(msg);
      }

      const publicUrl = data?.publicUrl || data?.url || data?.imageUrl || "";
      if (!publicUrl) throw new Error("upload response missing publicUrl");

      setForm((p) => ({ ...p, imageUrl: publicUrl }));
      setUploadInfo({ publicUrl });

      console.log("[ads] backend upload ok:", { publicUrl });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setUploading(false);
    }
  };

  const onCreate = async () => {
    setErr("");
    setLoading(true);
    try {
      const payload = {
        title: form.title || null,
        imageUrl: form.imageUrl || null,
        targetUrl: form.targetUrl || null,
        placement: form.placement,
        startAt: localInputToIso(form.startAt),
        endAt: localInputToIso(form.endAt),
        status: form.status,
      };

      if (!payload.placement) throw new Error("Потрібно обрати розміщення");
      if (!payload.startAt) throw new Error("Некоректний час початку");
      if (!payload.endAt) throw new Error("Некоректний час завершення");
      if (!payload.imageUrl) throw new Error("Спочатку завантаж банер (S3)");

      const st = Date.parse(payload.startAt);
      const en = Date.parse(payload.endAt);
      if (!Number.isFinite(st) || !Number.isFinite(en)) throw new Error("Некоректні дати");
      if (en <= st) throw new Error("Кінець має бути пізніше за початок");

      await fetchJson(`${API_BASE}/api/ads/campaigns`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setForm((p) => ({
        ...p,
        title: "",
        targetUrl: "",
        startAt: "",
        endAt: "",
        status: "paused",
        imageUrl: "",
      }));

      setFile(null);
      setUploadInfo(null);

      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (it) => {
    if (!it || !it.id) return;
    setErr("");
    setLoading(true);
    try {
      const next = it.status === "active" ? "paused" : "active";
      await fetchJson(`${API_BASE}/api/ads/campaigns/${encodeURIComponent(it.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // report actions
  const openReportFor = async (it) => {
    if (!it || !it.id) return;
    setReportCampaignId(it.id);
    setReportFrom(daysAgoKeyLocal(6));
    setReportTo(todayKeyLocal());
    setReportData(null);
    setReportErr("");
    setReportOpen(true);

    // reset share UI when opening report
    setShareLoading(false);
    setShareErr("");
    setShareUrl("");
    setShareOk("");
  };

  const loadReport = async () => {
    if (!reportCampaignId) {
      setReportErr("Потрібен ID кампанії");
      return;
    }
    if (!reportFrom || !/^\d{4}-\d{2}-\d{2}$/.test(reportFrom)) {
      setReportErr("Некоректна дата 'Від' (формат YYYY-MM-DD)");
      return;
    }
    if (!reportTo || !/^\d{4}-\d{2}-\d{2}$/.test(reportTo)) {
      setReportErr("Некоректна дата 'До' (формат YYYY-MM-DD)");
      return;
    }
    if (reportTo < reportFrom) {
      setReportErr("Дата 'До' має бути не раніше за 'Від'");
      return;
    }

    setReportErr("");
    setReportLoading(true);
    try {
      const url =
        `${API_BASE}/api/ads/report` +
        `?campaignId=${encodeURIComponent(reportCampaignId)}` +
        `&from=${encodeURIComponent(reportFrom)}` +
        `&to=${encodeURIComponent(reportTo)}`;

      const data = await fetchJson(url);
      setReportData(data || null);

      setShareErr("");
      setShareUrl("");
      setShareOk("");
    } catch (e) {
      setReportErr(String(e.message || e));
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  // create share link (token+TTL) + copy
  const createShareLink = async () => {
    if (!reportCampaignId) {
      setShareErr("Потрібен ID кампанії");
      return;
    }
    if (!reportFrom || !/^\d{4}-\d{2}-\d{2}$/.test(reportFrom)) {
      setShareErr("Некоректна дата 'Від' (YYYY-MM-DD)");
      return;
    }
    if (!reportTo || !/^\d{4}-\d{2}-\d{2}$/.test(reportTo)) {
      setShareErr("Некоректна дата 'До' (YYYY-MM-DD)");
      return;
    }
    if (reportTo < reportFrom) {
      setShareErr("Дата 'До' має бути не раніше за 'Від'");
      return;
    }

    setShareErr("");
    setShareOk("");
    setShareLoading(true);

    try {
      const data = await fetchJson(`${API_BASE}/api/ads/report-share`, {
        method: "POST",
        body: JSON.stringify({
          campaignId: reportCampaignId,
          from: reportFrom,
          to: reportTo,
          ttlDays: 7,
        }),
      });

      const apiUrl = data?.shareUrl || "";
if (!apiUrl) throw new Error("Не вдалося сформувати посилання");

const token = apiUrl.split("/").filter(Boolean).pop();

const publicUrl = `${window.location.origin}/ad-report/${encodeURIComponent(token)}`;

setShareUrl(publicUrl);

await copyToClipboard(publicUrl);
setShareOk("✅ Посилання скопійовано");

console.log("[ads] share link created:", {
  apiUrl,
  publicUrl,
});

    } catch (e) {
      setShareErr(String(e.message || e));
      setShareOk("");
    } finally {
      setShareLoading(false);
    }
  };

  const reportTotals = reportData?.totals || null;
  const reportDays = reportData?.days || [];

  const ctr = useMemo(() => {
    const impr = Number(reportTotals?.impressions || 0);
    const clk = Number(reportTotals?.clicks || 0);
    if (!impr) return 0;
    return (clk / impr) * 100;
  }, [reportTotals]);

  return (
    <>
      <DynamicHeader
        title="Кабінет реклами"
        variant="glass"
        size="regular"
        hideOnScroll={false}
        showBack={true}
        showMenu={false}
        withSpacer={false}
      />
      <div className="local-header-spacer" />

      <div className="cv-ads-cabinet-wrap">
        <div className="cv-ads-card">
          <div className="cv-ads-title">Створити кампанію</div>

          {err ? <div className="cv-ads-error">{err}</div> : null}

          <div className="cv-ads-grid">
            <div className="cv-ads-field">
              <div className="cv-ads-label">Назва</div>
              <input
                value={form.title}
                onChange={(e) => onChange("title", e.target.value)}
                placeholder="Напр. RAMON спальня Bristol"
              />
            </div>

            <div className="cv-ads-field">
              <div className="cv-ads-label">Розміщення</div>
              <select value={form.placement} onChange={(e) => onChange("placement", e.target.value)}>
                {placements.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
              <div className="cv-ads-hint">Ключ: <span className="mono">{form.placement}</span></div>
            </div>

            {/* upload block */}
            <div className="cv-ads-field">
              <div className="cv-ads-label">Банер (файл)</div>
              <input className="cv-ads-file" type="file" accept="image/*" onChange={onPickFile} />

              <div className="cv-ads-upload-row">
                <button
                  className="cv-ads-btn small"
                  type="button"
                  disabled={uploading || !file}
                  onClick={uploadBannerViaBackend}
                >
                  {uploading ? "Завантаження..." : "Завантажити"}
                </button>

                {uploadInfo?.publicUrl ? <div className="cv-ads-upload-ok">✅ Банер готовий</div> : null}
              </div>

              {form.imageUrl ? (
                <div className="cv-ads-preview">
                  <img src={form.imageUrl} alt="preview" />
                </div>
              ) : null}
            </div>

            <div className="cv-ads-field">
              <div className="cv-ads-label">Посилання (куди вести)</div>
              <input
                value={form.targetUrl}
                onChange={(e) => onChange("targetUrl", e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="cv-ads-field">
              <div className="cv-ads-label">Початок</div>
              <input type="datetime-local" value={form.startAt} onChange={(e) => onChange("startAt", e.target.value)} />
            </div>

            <div className="cv-ads-field">
              <div className="cv-ads-label">Кінець</div>
              <input type="datetime-local" value={form.endAt} onChange={(e) => onChange("endAt", e.target.value)} />
            </div>

            <div className="cv-ads-field">
              <div className="cv-ads-label">Статус</div>
              <select value={form.status} onChange={(e) => onChange("status", e.target.value)}>
                <option value="paused">Пауза</option>
                <option value="active">Активна</option>
              </select>
            </div>

            <div className="cv-ads-field cv-ads-actions">
              <button className="cv-ads-btn" disabled={loading} onClick={onCreate}>
                {loading ? "..." : "Створити"}
              </button>
              <button className="cv-ads-btn secondary" disabled={loading} onClick={load}>
                Оновити
              </button>
            </div>
          </div>
        </div>

        <div className="cv-ads-card">
          <div className="cv-ads-title">Кампанії</div>

          <div className="cv-ads-list">
            {items && items.length ? (
              items.map((it) => {
                const impr = Number(it.impressions || 0);
                const clk = Number(it.clicks || 0);
                const ctrRow = impr ? (clk / impr) * 100 : 0;

                return (
                  <div className="cv-ads-row" key={it.id}>
                    <div className="cv-ads-row-main">
                      <div className="cv-ads-row-head">
                        <div className="cv-ads-row-name">{it.title || it.id}</div>
                        <div className={`cv-ads-pill ${it.status === "active" ? "active" : "paused"}`}>
                          {it.status === "active" ? "Активна" : "Пауза"}
                        </div>
                      </div>

                      <div className="cv-ads-row-sub">
                        <span className="muted">{placementLabelByKey[it.placement] || it.placement}</span>
                        <span className="dot">•</span>
                        <span className="muted">
                          {formatDateTimeUk(it.startAt)} → {formatDateTimeUk(it.endAt)}
                        </span>
                      </div>

                      <div className="cv-ads-row-metrics">
                        <div className="m">Покази: {formatNumber(impr)}</div>
                        <div className="m">Кліки: {formatNumber(clk)}</div>
                        <div className="m">CTR: {ctrRow.toFixed(2)}%</div>
                      </div>

                      {it.imageUrl ? (
                        <div className="cv-ads-row-preview">
                          <img src={it.imageUrl} alt={it.title || "ad"} />
                        </div>
                      ) : null}
                    </div>

                    <div className="cv-ads-row-side">
                      <button className="cv-ads-btn small" disabled={loading} onClick={() => toggleStatus(it)}>
                        {it.status === "active" ? "Пауза" : "Активувати"}
                      </button>

                      <button
                        className="cv-ads-btn small secondary"
                        disabled={loading}
                        onClick={() => openReportFor(it)}
                        style={{ marginTop: 8 }}
                      >
                        Звіт
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="cv-ads-empty">Немає кампаній</div>
            )}
          </div>
        </div>

        {/* Report modal */}
        {reportOpen ? (
          <div
            className="cv-ads-report-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setReportOpen(false);
            }}
          >
            <div className="cv-ads-report-modal">
              <div className="cv-ads-report-top">
                <div className="cv-ads-report-h">
                  <div className="cv-ads-title" style={{ marginBottom: 0 }}>
                    Звіт кампанії
                  </div>
                  <div className="cv-ads-report-sub">
                    <span className="pill">
                      Період: {formatDayUk(reportFrom)} — {formatDayUk(reportTo)}
                    </span>
                    {reportCampaignId ? (
                      <span className="pill soft">
                        Кампанія: <span className="mono">{reportCampaignId}</span>
                      </span>
                    ) : null}
                  </div>
                </div>

                <button className="cv-ads-btn secondary" disabled={reportLoading} onClick={() => setReportOpen(false)}>
                  Закрити
                </button>
              </div>

              {reportErr ? <div className="cv-ads-error" style={{ marginBottom: 10 }}>{reportErr}</div> : null}

              <div className="cv-ads-grid" style={{ marginTop: 0 }}>
                <div className="cv-ads-field">
                  <div className="cv-ads-label">ID кампанії</div>
                  <input value={reportCampaignId} onChange={(e) => setReportCampaignId(e.target.value)} />
                </div>

                <div className="cv-ads-field">
                  <div className="cv-ads-label">Від (YYYY-MM-DD)</div>
                  <input value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
                </div>

                <div className="cv-ads-field">
                  <div className="cv-ads-label">До (YYYY-MM-DD)</div>
                  <input value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
                </div>

                <div className="cv-ads-field cv-ads-actions">
                  <button className="cv-ads-btn" disabled={reportLoading} onClick={loadReport}>
                    {reportLoading ? "..." : "Показати"}
                  </button>

                  <button
                    className="cv-ads-btn secondary"
                    disabled={shareLoading}
                    onClick={createShareLink}
                    title="Створити публічне посилання на цей звіт і скопіювати"
                    style={{ marginLeft: 10 }}
                  >
                    {shareLoading ? "..." : "Поділитись"}
                  </button>
                </div>
              </div>

              {/* share output */}
              {shareErr ? <div className="cv-ads-error" style={{ marginTop: 10 }}>{shareErr}</div> : null}
              {shareOk ? <div className="cv-ads-upload-ok" style={{ marginTop: 10 }}>{shareOk}</div> : null}

              {shareUrl ? (
                <div className="cv-ads-share-box">
                  <div className="cv-ads-label" style={{ marginBottom: 6 }}>Публічне посилання на звіт</div>
                  <div className="cv-ads-share-row">
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="cv-ads-share-link">
                      {shareUrl}
                    </a>
                    <button
                      className="cv-ads-btn small secondary"
                      type="button"
                      onClick={async () => {
                        try {
                          await copyToClipboard(shareUrl);
                          setShareOk("✅ Посилання скопійовано");
                        } catch (e) {
                          setShareErr(String(e.message || e));
                        }
                      }}
                    >
                      Копіювати
                    </button>
                  </div>
                  <div className="cv-ads-hint">
                    Посилання живе обмежений час (за замовчуванням 7 днів). Якщо треба — зробимо вибір TTL.
                  </div>
                </div>
              ) : null}

              {reportData ? (
                <div style={{ marginTop: 14 }}>
                  <div className="cv-ads-kpis">
                    <div className="kpi">
                      <div className="kpi-label">Покази</div>
                      <div className="kpi-value">{formatNumber(reportTotals?.impressions || 0)}</div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">Кліки</div>
                      <div className="kpi-value">{formatNumber(reportTotals?.clicks || 0)}</div>
                    </div>
                    <div className="kpi">
                      <div className="kpi-label">CTR</div>
                      <div className="kpi-value">{ctr.toFixed(2)}%</div>
                    </div>
                  </div>

                  <div className="cv-ads-report-table">
                    <div className="cv-ads-report-head">
                      <div>Дата</div>
                      <div>Покази</div>
                      <div>Кліки</div>
                      <div>CTR</div>
                    </div>

                    {reportDays && reportDays.length ? (
                      reportDays.map((d) => {
                        const impr = Number(d.impressions || 0);
                        const clk = Number(d.clicks || 0);
                        const c = impr ? (clk / impr) * 100 : 0;
                        return (
                          <div className="cv-ads-report-row" key={d.day}>
                            <div className="day">{formatDayUk(d.day)}</div>
                            <div>{formatNumber(impr)}</div>
                            <div>{formatNumber(clk)}</div>
                            <div>{c.toFixed(2)}%</div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="cv-ads-empty" style={{ marginTop: 10 }}>
                        Немає даних за період
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {reportLoading ? (
                <div className="cv-ads-report-loading">
                  <div className="spinner" />
                  <div>Завантаження звіту…</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
